// ─────────────────────────────────────────────────────────────────────────────
// AudioInputPrefs — B7 (design rev F): the mic settings panel for the voice lane,
// wired to the B daemon over the panel-role socket (voicePanelPrefs.ts). The old
// Flask REST binding (voiceAudioPrefs.ts) is vestigial — the store is B-owned.
//
// Four controls, each bound to a real B6 knob:
//   • NOISE SUPPRESSION  — ON/OFF → the typed DenoiseClient gate.
//   • INPUT SENSITIVITY  — DEFAULT (the evidence-cited 0.50) / MANUAL slider,
//     clamped canonically at the store: displayed = stored = applied. The label
//     claims NO adaptivity — DEFAULT means the fixed default threshold.
//   • ECHO CANCELLATION  — ON/OFF → the per-block Speex bypass (L2 flip row).
//   • INPUT DEVICE       — selector over the daemon's ALSA enumeration; the panel
//     shows the device ACTUALLY open, never the request as if it had applied.
//
// Honest state (rev F §5): every render comes from {prefs, effective} returned by
// the applying authority — never optimistic. state_changed (the contentless
// nudge, sent to ALL subscribers including this one) triggers a re-read.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { REXY_FONT, REXY_CONSOLE } from '../lib/rexyConsole';
import {
  connectPanelPrefs, EffectiveState, InputDevice, PanelPrefs, PanelPrefsPatch,
  PanelPrefsSocket,
} from '../api/voicePanelPrefs';

const MONO = REXY_FONT;

// A 2-state segmented toggle in the console idiom (cyan on-default / gold off-default).
function Seg2(props: {
  value: boolean; on: string; off: string; disabled?: boolean;
  offIsGold?: boolean; neutral?: boolean; onSelect: (v: boolean) => void;
}) {
  const { value, on, off, disabled, offIsGold, neutral, onSelect } = props;
  const opts: { v: boolean; label: string }[] = [{ v: true, label: on }, { v: false, label: off }];
  return (
    <div style={styles.segWrap} onClick={(e) => e.stopPropagation()}>
      {opts.map((o) => {
        const active = value === o.v;
        // T1 fix (rev B.1 ii): while DISCONNECTED the active segment renders
        // neutral — an active-looking colored toggle over a dead socket is a
        // lying green; disabled alone is not enough. Panel-local prop.
        const accent = neutral ? REXY_CONSOLE.dimText
          : o.v ? REXY_CONSOLE.primary : (offIsGold ? REXY_CONSOLE.gold : REXY_CONSOLE.primary);
        const activeBg = neutral ? 'transparent'
          : o.v ? REXY_CONSOLE.badgeOkBg : (offIsGold ? REXY_CONSOLE.badgeWrnBg : REXY_CONSOLE.badgeOkBg);
        return (
          <button
            key={o.label}
            onClick={() => onSelect(o.v)}
            aria-pressed={active}
            disabled={disabled}
            style={{
              ...styles.seg,
              color: active ? accent : REXY_CONSOLE.dimText,
              background: active ? activeBg : 'transparent',
              boxShadow: active ? 'inset 0 1px 2px rgba(0,0,0,0.55), 0 1px 0 rgba(255,255,255,0.05)' : 'none',
              cursor: disabled ? 'default' : 'pointer',
              opacity: disabled && !active ? 0.55 : 1,
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export default function AudioInputPrefs() {
  const [prefs, setPrefsState] = useState<PanelPrefs | null>(null);
  const [effective, setEffective] = useState<EffectiveState | null>(null);
  const [devices, setDevices] = useState<InputDevice[]>([]);
  const [sens, setSens] = useState(0.5);   // local slider position during drag only
  const [pending, setPending] = useState(false);
  const [err, setErr] = useState(false);
  const sockRef = useRef<PanelPrefsSocket | null>(null);

  const applySnapshot = useCallback((s: { prefs: PanelPrefs; effective: EffectiveState }) => {
    setPrefsState(s.prefs);
    setEffective(s.effective);
    // The slider/marker renders the APPLIED threshold, never the request (Ghost
    // corrected-artifact block 3): after set_prefs the thumb snaps to the old
    // effective value until the applying lane's nudge-re-read converges it.
    // During a drag the local state tracks the thumb; every snapshot re-anchors.
    setSens(s.effective.threshold);
    setErr(false);
  }, []);

  const reread = useCallback(() => {
    sockRef.current?.getPrefs().then(applySnapshot).catch(() => setErr(true));
  }, [applySnapshot]);

  useEffect(() => {
    let alive = true;
    let sock: PanelPrefsSocket | null = null;
    connectPanelPrefs({
      onStateChanged: () => { if (alive) reread(); },   // the nudge: re-read, only
      onClose: () => { if (alive) setErr(true); },
    })
      .then((s) => {
        if (!alive) { s.close(); return; }
        sock = s;
        sockRef.current = s;
        s.getPrefs().then((snap) => { if (alive) applySnapshot(snap); })
          .catch(() => { if (alive) setErr(true); });
        s.listInputDevices().then((d) => { if (alive) setDevices(d); })
          .catch(() => { /* enumeration is a convenience, never a dependency */ });
      })
      .catch(() => { if (alive) setErr(true); });
    return () => { alive = false; sockRef.current = null; sock?.close(); };
  }, [applySnapshot, reread]);

  const push = (patch: PanelPrefsPatch) => {
    const sock = sockRef.current;
    if (!sock || !prefs) return;
    setPending(true);
    sock.setPrefs(patch)
      .then(applySnapshot)          // honest as-of-now state; the nudge converges it
      .catch(() => setErr(true))
      .finally(() => setPending(false));
  };

  const disabled = pending || !prefs || err;
  const degraded = effective?.front === 'degraded';
  // Control ACTIVE states derive from EFFECTIVE, never the request (Ghost JOINT
  // finding 2 — rendering prefs showed a toggle as applied while L2 still ran the
  // old state). A clicked control settles when the applying lane's nudge-re-read
  // converges (≤ one L2 wake); until then it honestly shows what is in effect.
  const noiseOn = effective?.noise_gate ?? true;
  const manual = effective?.sensitivity_mode === 'manual';
  const aecOn = effective ? effective.aec !== 'bypassed' : true;
  const pct = Math.round(sens * 100);

  // Honest hints, from EFFECTIVE state (never the pref):
  const denoiserHint = err ? 'UNKNOWN'
    : effective?.denoiser === 'up' ? 'DEEPFILTERNET'
    : effective?.denoiser === 'disabled' ? 'OFF (BY CHOICE)'
    : noiseOn ? 'ON — SIDECAR DOWN (PASSTHROUGH)' : 'SIDECAR DOWN';
  const aecHint = err ? 'UNKNOWN'
    : degraded ? 'FRONT DEGRADED'
    : effective?.aec === 'bypassed' ? 'BYPASSED' : 'SPEEX';
  const actualDevice = effective?.input_device.actual ?? null;
  const deviceError = effective?.input_device.error ?? null;
  // T1 fix (rev B.1 iii): under err the hint is UNKNOWN — a retained last
  // applied threshold over a dead socket would render stale state as current.
  const thresholdHint = err ? 'UNKNOWN' : effective
    ? (manual ? `${Math.round(effective.threshold * 100)}%` : 'DEFAULT · 0.50')
    : '…';

  return (
    <div style={styles.wrap}>
      {/* T1 fix: daemon-absent is a NORMAL panel state — the app boots; the
          panel says why it is inactive (one state for never-connected and
          connection-lost; recovery is daemon-up + reload). */}
      {err && (
        <div style={styles.degradedChip}>
          VOICE DAEMON NOT CONNECTED — PANEL INACTIVE
        </div>
      )}
      {degraded && !err && (
        <div style={styles.degradedChip}>FRONT DEGRADED — INTERIM VAD</div>
      )}

      {/* ── NOISE SUPPRESSION ───────────────────────────────────────────── */}
      <div style={styles.block}>
        <div style={styles.rowTop}>
          <span style={styles.label}>NOISE SUPPRESSION</span>
          <span style={{ ...styles.hint,
            color: err || (noiseOn && effective?.denoiser === 'down')
              ? REXY_CONSOLE.warning : REXY_CONSOLE.dimText }}>
            {denoiserHint}
          </span>
        </div>
        <Seg2
          value={noiseOn} on="ON" off="OFF" offIsGold disabled={disabled} neutral={err}
          onSelect={(v) => { if (v !== noiseOn) push({ noise_suppression: v }); }}
        />
      </div>

      {/* ── INPUT SENSITIVITY — DEFAULT threshold / MANUAL slider ───────── */}
      <div style={styles.block}>
        <div style={styles.rowTop}>
          <span style={styles.label}>INPUT SENSITIVITY</span>
          <span style={styles.hint}>{err ? 'UNKNOWN' : degraded ? 'FRONT DEGRADED' : thresholdHint}</span>
        </div>

        <div style={{ ...styles.barOuter, opacity: (!manual || degraded || err) ? 0.45 : 1 }}>
          <div style={styles.barGradient} />
          <div style={{ ...styles.marker, left: `${pct}%` }} />
          <input
            type="range" min={0} max={1} step={0.01} value={sens}
            disabled={disabled || !manual || degraded}
            aria-label="Input sensitivity threshold"
            onChange={(e) => setSens(parseFloat(e.target.value))}
            onMouseUp={() => push({ sensitivity: sens })}
            onTouchEnd={() => push({ sensitivity: sens })}
            onKeyUp={() => push({ sensitivity: sens })}
            style={styles.range}
          />
        </div>
        <div style={styles.subRow}>
          <span style={styles.sub}>BELOW · IGNORED</span>
          <span style={styles.sub}>ABOVE · SPEECH</span>
        </div>

        <div style={{ ...styles.rowTop, marginTop: 8 }}>
          {/* DEFAULT = the fixed default threshold (0.50). Not adaptive — the
              label says exactly what the component does (rein-in 2). */}
          <span style={styles.subLabel}>DEFAULT THRESHOLD</span>
          <Seg2
            value={!manual} on="DEFAULT" off="MANUAL" offIsGold neutral={err}
            disabled={disabled || degraded}
            onSelect={(v) => {
              const mode = v ? 'default' : 'manual';
              if ((prefs?.sensitivity_mode ?? 'default') !== mode) {
                push({ sensitivity_mode: mode });
              }
            }}
          />
        </div>
      </div>

      {/* ── ECHO CANCELLATION ───────────────────────────────────────────── */}
      <div style={styles.block}>
        <div style={styles.rowTop}>
          <span style={styles.label}>ECHO CANCELLATION</span>
          <span style={{ ...styles.hint,
            color: err ? REXY_CONSOLE.warning : REXY_CONSOLE.dimText }}>
            {aecHint}
          </span>
        </div>
        <Seg2
          value={aecOn} on="ON" off="OFF" offIsGold disabled={disabled || degraded} neutral={err}
          onSelect={(v) => { if (v !== aecOn) push({ echo_cancel: v }); }}
        />
      </div>

      {/* ── INPUT DEVICE ────────────────────────────────────────────────── */}
      <div style={styles.block}>
        <div style={styles.rowTop}>
          <span style={styles.label}>INPUT DEVICE</span>
          <span style={{ ...styles.hint,
            color: deviceError ? REXY_CONSOLE.warning : REXY_CONSOLE.dimText }}>
            {err ? 'UNKNOWN' : (actualDevice ?? '…')}
          </span>
        </div>
        <select
          value={prefs?.input_device ?? ''}
          disabled={disabled}
          aria-label="Input device"
          onChange={(e) => push({ input_device: e.target.value || null })}
          style={styles.select}
        >
          <option value="">SYSTEM ORDER (DEFAULT)</option>
          {devices.map((d) => (
            <option key={d.id} value={d.id}>
              {d.description}{deviceError?.requested === d.id ? ' — UNAVAILABLE' : ''}
            </option>
          ))}
          {/* a requested device missing from enumeration still renders, marked */}
          {prefs?.input_device && !devices.some((d) => d.id === prefs.input_device) && (
            <option value={prefs.input_device}>
              {prefs.input_device}{deviceError ? ' — UNAVAILABLE' : ''}
            </option>
          )}
        </select>
        {deviceError && (
          <div style={styles.deviceErr}>
            {`${deviceError.requested}: ${deviceError.error} — using ${actualDevice ?? 'system order'}`}
          </div>
        )}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  wrap: { borderTop: `1px solid ${REXY_CONSOLE.divider}`, marginTop: 12, paddingTop: 12 },
  block: { marginBottom: 12 },
  rowTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 },
  label: { fontFamily: MONO, fontSize: 11, letterSpacing: 1.5, color: REXY_CONSOLE.primary },
  subLabel: { fontFamily: MONO, fontSize: 9, letterSpacing: 1, color: REXY_CONSOLE.mutedText, alignSelf: 'center' },
  hint: { fontFamily: MONO, fontSize: 9, letterSpacing: 1 },
  degradedChip: {
    fontFamily: MONO, fontSize: 9, letterSpacing: 1.5, color: REXY_CONSOLE.warning,
    background: REXY_CONSOLE.badgeWrnBg, border: `1px solid ${REXY_CONSOLE.cardBorder}`,
    borderRadius: 4, padding: '4px 8px', marginBottom: 10, textAlign: 'center',
    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.45)',
  },
  segWrap: {
    display: 'flex', gap: 3, padding: 3, borderRadius: 999,
    border: `1px solid ${REXY_CONSOLE.cardBorder}`, background: REXY_CONSOLE.panel,
    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.45), 0 1px 0 rgba(255,255,255,0.04)',
    minWidth: 120,
  },
  seg: {
    flex: 1, fontFamily: MONO, fontSize: 9, fontWeight: 700, letterSpacing: 1.5,
    textTransform: 'uppercase', padding: '5px 6px', borderRadius: 999, border: 'none',
    transition: 'all 0.15s ease',
  },
  barOuter: {
    position: 'relative', height: 16, borderRadius: 4, overflow: 'hidden',
    border: `1px solid ${REXY_CONSOLE.cardBorder}`,
    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.5)',
  },
  barGradient: {
    position: 'absolute', inset: 0,
    background: 'linear-gradient(90deg, rgba(34,197,94,0.55) 0%, rgba(255,215,0,0.55) 70%, rgba(239,1,7,0.6) 100%)',
  },
  marker: {
    position: 'absolute', top: -1, bottom: -1, width: 2, marginLeft: -1,
    background: REXY_CONSOLE.secondary, boxShadow: '0 0 4px rgba(255,255,255,0.6)',
  },
  range: {
    position: 'absolute', inset: 0, width: '100%', height: '100%',
    margin: 0, opacity: 0, cursor: 'pointer',
  },
  subRow: { display: 'flex', justifyContent: 'space-between', marginTop: 4 },
  sub: { fontFamily: MONO, fontSize: 8, letterSpacing: 1, color: REXY_CONSOLE.dimText },
  select: {
    width: '100%', fontFamily: MONO, fontSize: 10, letterSpacing: 1,
    color: REXY_CONSOLE.primary, background: REXY_CONSOLE.panel,
    border: `1px solid ${REXY_CONSOLE.cardBorder}`, borderRadius: 4,
    padding: '6px 8px', outline: 'none',
    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.45)',
  },
  deviceErr: {
    fontFamily: MONO, fontSize: 8, letterSpacing: 0.5, color: REXY_CONSOLE.warning,
    marginTop: 5, lineHeight: 1.5,
  },
};
